import { CellDep, DepType, normalizers, OutPoint, Reader, SerializeWitnessArgs, transformers, } from '@lay2/pw-core';
import axios from 'axios';
import { getConfig } from './config';
import * as LumosCore from './js-scripts/lumos-core';
import * as UPLockWitness from './js-scripts/up-lock-witness';
/**
 * fetch UniPass smt proof/cell deps/user info from UniPass snapshot server
 *
 * @param usernameHash UniPass username sha256 hash
 * @returns formated AssetLockProof
 */
export async function fetchAssetLockProof(usernameHash) {
    const data = await axios.post(getConfig().upSnapshotUrl, {
        jsonrpc: '2.0',
        method: 'get_asset_lock_tx_info',
        params: [usernameHash],
        id: '1',
    });
    // convert data to AssetLockProf
    const { cell_deps, lock_info, user_info_smt_proof } = data.data.result;
    const cellDeps = [];
    for (const { dep_type, out_point: { tx_hash, index }, } of cell_deps) {
        const depType = dep_type === DepType.code ? DepType.code : DepType.depGroup;
        cellDeps.push(new CellDep(depType, new OutPoint(tx_hash, index)));
    }
    const lockInfo = [];
    for (const { user_info, username } of lock_info) {
        lockInfo.push({ userInfo: user_info, username });
    }
    const proof = {
        cellDeps,
        lockInfo,
        userInfoSmtProof: user_info_smt_proof,
    };
    return proof;
}
/**
 * complete transaction with UniPass smt proof
 *
 * @param signedTx signed transaction
 * @param assetLockProof UniPass smt proof from UniPass snapshot server url
 * @param usernameHash UniPass username sha256 hash
 * @returns
 */
export function completeTxWithProof(signedTx, assetLockProof, usernameHash) {
    const { pubkey, sig } = extractSigFromWitness(signedTx.witnesses[0]);
    signedTx.raw.cellDeps.push(...assetLockProof.cellDeps);
    // push asset-lock bin as cell deps
    // rebuild witness, username/userinfo/proof
    const witnessLock = UPLockWitness.SerializeAssetLockWitness({
        pubkey,
        sig,
        username: new Reader(usernameHash),
        user_info: new Reader(assetLockProof.lockInfo[0].userInfo),
        user_info_smt_proof: new Reader(assetLockProof.userInfoSmtProof),
    });
    // Fill witnesses
    signedTx.witnesses[0] = new Reader(SerializeWitnessArgs(normalizers.NormalizeWitnessArgs({
        ...signedTx.witnessArgs[0],
        lock: witnessLock,
    }))).serializeJson();
    return signedTx;
}
/**
 * decode UniPass pubkey and signature from witness.lock in hex format
 *
 * @param witness CKB transaction witness from PWCore signer
 * @returns pubkey in molecule format
 */
function extractSigFromWitness(witness) {
    const witnessArgs = new LumosCore.WitnessArgs(new Reader(witness));
    const lockHex = new Reader(witnessArgs.getLock().value().raw()).serializeJson();
    const { keyType, pubkey, sig } = JSON.parse(Buffer.from(lockHex.replace('0x', ''), 'hex').toString());
    // convert type to UPAuthResponse
    let pubKeyValue;
    switch (keyType) {
        case 'RsaPubkey':
            pubKeyValue = {
                e: new Reader(pubkey.slice(0, 10)),
                n: new Reader(`0x${pubkey.slice(10)}`),
            };
            break;
        case 'Secp256k1Pubkey':
            pubKeyValue = new Reader(pubkey);
            break;
        case 'Secp256r1Pubkey':
            break;
    }
    const pubKey = {
        type: keyType,
        value: pubKeyValue,
    };
    return { pubkey: pubKey, sig: new Reader(sig) };
}
/**
 * complete transaction with cell deps and witness including smt proof
 * and send transaction to ckb chain
 *
 * @param usernameHash UniPass username sha256 hash
 * @param signedTx signed CKB transaction
 * @param rpc
 * @returns CKB transaction hash
 */
export async function sendUPLockTransaction(usernameHash, signedTx, rpc) {
    // fetch cellDeps/userinfo/proof from aggregator
    const assetLockProof = await fetchAssetLockProof(usernameHash);
    if (new Reader(assetLockProof.lockInfo[0].userInfo).length() === 0) {
        throw new Error('user not registered');
    }
    // fill tx cell deps and witness
    const completedSignedTx = completeTxWithProof(signedTx, assetLockProof, usernameHash);
    const transformedTx = transformers.TransformTransaction(completedSignedTx);
    const txHash = await rpc.send_transaction(transformedTx, 'passthrough');
    return txHash;
}
//# sourceMappingURL=data:application/json;base64,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